import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { HeartPulse, Moon, Plus, RefreshCw, ShieldCheck, Sparkles } from 'lucide-react';
import i18nInstance from '../../../i18n';


const t = (key, fallback, options = {}) =>
  i18nInstance.t(key, typeof fallback === 'string' ? { defaultValue: fallback, ...options } : fallback);
const TOOLTIP_STYLE = {
  backgroundColor: '#18181B',
  border: '1px solid rgba(190, 242, 100, 0.28)',
  borderRadius: '0.5rem',
  color: '#F9FAFB',
};

const Metric = ({ label, value }) => (
  <div className="rounded-lg bg-zinc-50 p-3">
    <p className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">{label}</p>
    <p className="mt-1 text-lg font-black text-zinc-950">{value}</p>
  </div>
);

const RecoveryRow = ({ log, t, locale }) => (
  <article className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm" data-testid={`recovery-row-${log.id}`}>
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-xs font-black uppercase tracking-[0.12em] text-emerald-700">
          {new Date(log.date).toLocaleDateString(locale, { day: '2-digit', month: 'short' })}
        </p>
        <h3 className="mt-1 font-black text-zinc-950">{t('fitness.recoveryLogs')}</h3>
      </div>
      <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100">
        <HeartPulse size={22} strokeWidth={1.8} />
      </div>
    </div>
    <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
      <Metric label={t('fitness.sleepHours')} value={`${log.sleep_hours ?? '-'} h`} />
      <Metric label={t('fitness.sleepQuality')} value={log.sleep_quality ?? '-'} />
      <Metric label={t('fitness.fatigue')} value={log.fatigue_level ?? '-'} />
      <Metric label={t('fitness.soreness')} value={log.muscle_soreness ?? '-'} />
      <Metric label={t('fitness.stress')} value={log.stress_level ?? '-'} />
      {log.resting_heart_rate != null ? (
        <Metric label={t('fitness.restingHR')} value={`${log.resting_heart_rate} ${t('fitness.heartRateUnit')}`} />
      ) : null}
    </div>
    {log.notes ? <p className="mt-4 rounded-lg bg-zinc-50 p-3 text-sm leading-6 text-zinc-600">{log.notes}</p> : null}
  </article>
);

const ReadinessScoreCard = ({ readiness, latestLog, t }) => (
  <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 text-emerald-950 shadow-sm">
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-xs font-black uppercase tracking-[0.14em] text-emerald-700">{t('fitness.readinessScore')}</p>
        <p className="mt-2 text-5xl font-black tracking-normal">{readiness === null ? '-' : readiness}</p>
        <p className="mt-1 text-sm leading-6 opacity-85">
          {latestLog ? t('fitness.recoveryLogs') : t('common.noData')}
        </p>
      </div>
      <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-white text-emerald-700 ring-1 ring-emerald-100">
        <ShieldCheck size={28} strokeWidth={1.8} />
      </div>
    </div>
    <div className="mt-5 h-3 rounded-full bg-white/70">
      <div className="h-3 rounded-full bg-emerald-400" style={{ width: `${readiness || 0}%` }} />
    </div>
  </section>
);

const RecoveryStatusCard = ({ recoveryStats, t }) => (
  <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
    <div className="mb-4 flex items-center gap-3">
      <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-blue-50 text-blue-700 ring-1 ring-blue-100">
        <Moon size={22} strokeWidth={1.8} />
      </div>
      <div>
        <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">{t('fitness.tabRecovery')}</p>
        <h2 className="text-xl font-black tracking-normal text-zinc-950">{t('fitness.recoveryTrend')}</h2>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Metric label={t('fitness.sleepHours')} value={`${recoveryStats?.avg_sleep_hours || 0} h`} />
      <Metric label={t('fitness.sleepQuality')} value={Math.round(recoveryStats?.avg_sleep_quality || 0)} />
      <Metric label={t('fitness.fatigue')} value={Math.round(recoveryStats?.avg_fatigue_level || 0)} />
      <Metric label={t('fitness.soreness')} value={Math.round(recoveryStats?.avg_muscle_soreness || 0)} />
    </div>
  </section>
);

const RecoveryTab = ({ recoveryStats, recoveryLogs, readiness, onLog, t, locale }) => {
  const chartData = useMemo(
    () =>
      recoveryLogs.slice(-14).reverse().map((log) => ({
        date: new Date(log.date).toLocaleDateString(locale, {
          day: '2-digit',
          month: 'short',
        }),
        sleep: log.sleep_hours || 0,
        fatigue: log.fatigue_level || 0,
        soreness: log.muscle_soreness || 0,
      })),
    [recoveryLogs, locale],
  );

  const latestLog = recoveryLogs[0] || null;

  return (
    <div className="space-y-5">
      <div className="grid gap-4 lg:grid-cols-[0.8fr_1.2fr]">
        <ReadinessScoreCard readiness={readiness} latestLog={latestLog} t={t} />
        <RecoveryStatusCard recoveryStats={recoveryStats} t={t} />
      </div>

      <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
              {t('fitness.recoveryTrend')}
            </p>
            <h2 className="mt-1 text-xl font-black tracking-normal text-zinc-950">
              {t('fitness.tabRecovery')}
            </h2>
          </div>
          <Sparkles size={20} className="text-zinc-400" strokeWidth={1.8} />
        </div>
        {chartData.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-8 text-center" data-testid="recovery-empty">
            <HeartPulse className="mx-auto text-zinc-400" size={42} strokeWidth={1.8} />
            <p className="mt-3 font-black text-zinc-950">{t('fitness.recoveryLogs')}</p>
            <p className="mt-1 text-sm text-zinc-500">{t('common.noData')}</p>
            <button
              type="button"
              onClick={onLog}
              className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 text-sm font-black text-emerald-700 transition hover:bg-emerald-100"
            >
              <Plus size={16} strokeWidth={1.8} />
              {t('fitness.logRecovery')}
            </button>
          </div>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                <XAxis dataKey="date" stroke="#71717A" tickLine={false} axisLine={false} />
                <YAxis stroke="#71717A" tickLine={false} axisLine={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Line type="monotone" dataKey="sleep" stroke="#2563EB" strokeWidth={2} name={t('fitness.sleepHours')} dot={false} />
                <Line type="monotone" dataKey="fatigue" stroke="#F43F5E" strokeWidth={2} name={t('fitness.fatigue')} dot={false} />
                <Line type="monotone" dataKey="soreness" stroke="#84CC16" strokeWidth={2} name={t('fitness.soreness')} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      {recoveryLogs.length > 0 ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {recoveryLogs.map((log) => (
            <RecoveryRow key={log.id} log={log} t={t} locale={locale} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default RecoveryTab;
